export interface ScoreBarProps {
  /**
   * Current score value. `null`/`undefined` means the voice has not been
   * scored (valid scores are 1..max), which renders a textual placeholder
   * instead of an empty bar.
   */
  score: number | null | undefined
  /** Maximum score the bar represents (defaults to 5). */
  max?: number
  /** Accessible name describing what the score measures (e.g. "Naturalness"). */
  label?: string
  /** Text rendered when the score is absent (e.g. "Not scored"). */
  notScoredLabel?: string
}

/**
 * A compact horizontal meter used to visualise a 1..max score
 * (e.g. voice naturalness / response speed). Shared by the customer-facing
 * voice picker and the internal voice admin list.
 */
export function ScoreBar({ score, max = 5, label, notScoredLabel }: ScoreBarProps) {
  if (score == null) {
    return (
      <span className="text-[11px] text-gray-400 italic dark:text-gray-500">
        {notScoredLabel ?? '—'}
      </span>
    )
  }
  const clamped = Math.max(0, Math.min(max, score))
  const pct = (clamped / max) * 100
  const valueText = `${clamped}/${max}`
  return (
    <div
      role="meter"
      aria-valuemin={0}
      aria-valuemax={max}
      aria-valuenow={clamped}
      aria-valuetext={valueText}
      aria-label={label}
      title={label ? `${label}: ${valueText}` : valueText}
      className="h-1.5 w-20 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700"
    >
      <div
        className="bg-brand-500 dark:bg-brand-400 h-full rounded-full transition-[width]"
        style={{ width: `${pct}%` }}
      />
    </div>
  )
}

export default ScoreBar
